import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';

// Interfaces
interface Prediction {
  id: number;
  influencer_id?: number;
  price_prediction: string;
  prediction_type: string;
  success_status: string;
  start_date: string | null;
  end_date: string | null;
  target_time: string | null;
  target_timestamp: string | null;
  prediction_made: string | null;
  source_url?: string;
  quote?: string;
  price_min?: number;
  price_max?: number;
  created_at: string;
  updated_at?: string;
  asset_type?: string;
  exclude_from_chart: boolean;
  alby_address?: string;
  vote_score?: number;
  manually_marked_success?: boolean;
  expert_name?: string;
}

interface EvaluationProposal {
  id: number;
  expert_name: string;
  x_handle?: string;
  prediction_type: string;
  target_price: number | null;
  price_min: number | null;
  price_max: number | null;
  current_status: string;
  suggested_status: string;
  reason: string;
  confidence: string;
  action_required: boolean;
  is_expired: boolean;
  days_left: number;
  end_date: string;
  quote?: string;
  source_url?: string;
  context: {
    creation_price: number | null;
    period_high: number;
    period_low: number;
    current_price: number;
  };
}

interface PredictionAudit {
  id: number;
  expert_name?: string;
  x_handle?: string;
  prediction_type: string;
  current_status: string | null;
  suggested_status: string;
  confidence: string;
  requires_manual_check: boolean;
  reason: string;
  issues: string[];
  quote?: string;
  source_url?: string | null;
  start_date: string | null;
  end_date: string | null;
  evaluation_period_start: string | null;
  evaluation_period_end: string | null;
  period_status: string | null;
  target_price: number | null;
  range_min: number | null;
  range_max: number | null;
  period_high: number | null;
  period_low: number | null;
  price_data_points: number | null;
  start_price: number | null;
  start_price_date: string | null;
  start_price_offset_days: number | null;
  prediction_direction: string | null;
  break_date: string | null;
  break_price: number | null;
}

interface PredictionManagerProps {
  theme: any;
}

// Styled Components
const Container = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #ffffff;
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
`;

const SearchBar = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 10px 15px;
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid #333;
  border-radius: 4px;
  background: ${props => props.active ? '#4299e1' : '#0f0f0f'};
  color: ${props => props.active ? '#ffffff' : '#888'};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#3182ce' : '#1a1a1a'};
    border-color: #4299e1;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: #0f0f0f;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #333;
`;

const StatNumber = styled.div`
  font-size: 1.8em;
  font-weight: bold;
  color: #4299e1;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #888;
  font-size: 0.9em;
`;

const PredictionCard = styled.div<{ excluded?: boolean }>`
  background: ${props => props.excluded ? '#1a0f0f' : '#0f0f0f'};
  border: 1px solid ${props => props.excluded ? '#663333' : '#333'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  opacity: ${props => props.excluded ? 0.7 : 1};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const ExpertInfo = styled.div`
  flex: 1;
`;

const ExpertName = styled.h3`
  color: #ffffff;
  margin: 0 0 5px 0;
  font-size: 18px;
`;

const PredictionDetails = styled.div`
  color: #888;
  font-size: 14px;
  margin: 5px 0;
`;

const QuoteBox = styled.div`
  background: #0a0a0a;
  border-left: 3px solid #4299e1;
  border-radius: 4px;
  padding: 12px 16px;
  margin: 10px 0;
  font-style: italic;
  color: #ccc;
  font-size: 14px;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' | 'success' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.2s;
  
  ${props => {
    switch(props.variant) {
      case 'danger':
        return `
          background: #dc2626;
          color: white;
          &:hover { background: #b91c1c; }
        `;
      case 'success':
        return `
          background: #16a34a;
          color: white;
          &:hover { background: #15803d; }
        `;
      default:
        return `
          background: #4299e1;
          color: white;
          &:hover { background: #3182ce; }
        `;
    }
  }}
`;

const EditForm = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 15px;
  margin-top: 15px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  color: #888;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`;

const Input = styled.input`
  padding: 8px 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const ToggleSwitch = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  
  input {
    width: 44px;
    height: 24px;
    appearance: none;
    background: #333;
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s;
    
    &:checked {
      background: #4299e1;
    }
    
    &::before {
      content: '';
      position: absolute;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: white;
      top: 3px;
      left: 3px;
      transition: transform 0.2s;
    }
    
    &:checked::before {
      transform: translateX(20px);
    }
  }
  
  span {
    color: #ccc;
    font-size: 14px;
  }
`;

const Badge = styled.span<{ status?: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  
  ${props => {
    switch(props.status) {
      case 'success':
        return 'background: rgba(22, 163, 74, 0.2); color: #16a34a;';
      case 'miss':
        return 'background: rgba(220, 38, 38, 0.2); color: #dc2626;';
      case 'pending':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      default:
        return 'background: rgba(107, 114, 128, 0.2); color: #6b7280;';
    }
  }}
`;

const TweetModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const TweetModalContent = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
`;

const TweetTextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 12px;
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 15px;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #1da1f2;
  }
`;

const CharCount = styled.div<{ over?: boolean }>`
  text-align: right;
  font-size: 13px;
  margin-top: 8px;
  color: ${props => props.over ? '#dc2626' : '#888'};
`;

const TweetPreview = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 16px;
  margin: 16px 0;
  
  h4 {
    color: #1da1f2;
    margin: 0 0 12px 0;
    font-size: 14px;
  }
  
  p {
    color: #e7e9ea;
    white-space: pre-wrap;
    margin: 0;
    font-size: 15px;
    line-height: 1.4;
  }
`;

const AuditMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin: 16px 0;
`;

const AuditStat = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 12px;

  .label {
    color: #888;
    font-size: 11px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .value {
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
  }
`;

const AuditReason = styled.div`
  background: #0f0f0f;
  border-left: 3px solid #4299e1;
  border-radius: 6px;
  padding: 12px;
  color: #ddd;
  font-size: 14px;
  line-height: 1.5;
  margin: 16px 0;
`;

const AuditIssueList = styled.div`
  margin: 12px 0;
  color: #f59e0b;
  font-size: 13px;
`;

// Evaluation UI Components
const EvaluationSection = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #0f3460;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const EvaluationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const EvaluationTitle = styled.h3`
  color: #e94560;
  margin: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MarketContext = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(0,0,0,0.3);
  border-radius: 8px;
`;

const MarketStat = styled.div`
  text-align: center;
  
  .value {
    font-size: 18px;
    font-weight: bold;
    color: #4cc9f0;
  }
  
  .label {
    font-size: 11px;
    color: #888;
    text-transform: uppercase;
  }
`;

const ProposalCard = styled.div<{ actionRequired?: boolean; suggestionType?: string }>`
  background: ${props => props.actionRequired ? 'rgba(233, 69, 96, 0.1)' : 'rgba(255,255,255,0.05)'};
  border: 1px solid ${props => {
    if (props.suggestionType === 'success') return '#16a34a';
    if (props.suggestionType === 'miss') return '#dc2626';
    return props.actionRequired ? '#e94560' : '#333';
  }};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const ProposalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ProposalInfo = styled.div`
  flex: 1;
`;

const ProposalName = styled.div`
  font-weight: bold;
  color: #fff;
  margin-bottom: 4px;
`;

const ProposalMeta = styled.div`
  font-size: 12px;
  color: #888;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProposalReason = styled.div`
  background: rgba(0,0,0,0.3);
  padding: 10px;
  border-radius: 6px;
  margin: 10px 0;
  font-size: 13px;
  color: #ccc;
  border-left: 3px solid ${props => props.color || '#4cc9f0'};
`;

const ProposalContext = styled.div`
  display: flex;
  gap: 16px;
  font-size: 11px;
  color: #888;
  margin-top: 8px;
`;

const SuggestionBadge = styled.span<{ type: string }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  
  ${props => {
    switch(props.type) {
      case 'success':
        return 'background: rgba(22, 163, 74, 0.2); color: #16a34a; border: 1px solid #16a34a;';
      case 'miss':
        return 'background: rgba(220, 38, 38, 0.2); color: #dc2626; border: 1px solid #dc2626;';
      default:
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308; border: 1px solid #eab308;';
    }
  }}
`;

const ApplyButton = styled.button<{ variant?: 'success' | 'miss' }>`
  padding: 6px 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s;
  
  ${props => {
    if (props.variant === 'success') {
      return `
        background: #16a34a;
        color: white;
        &:hover { background: #15803d; }
      `;
    }
    if (props.variant === 'miss') {
      return `
        background: #dc2626;
        color: white;
        &:hover { background: #b91c1c; }
      `;
    }
    return `
      background: #4299e1;
      color: white;
      &:hover { background: #3182ce; }
    `;
  }}
`;

const PredictionManager: React.FC<PredictionManagerProps> = ({ theme }) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [filteredPredictions, setFilteredPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterAsset, setFilterAsset] = useState<string>('all');
  const [showExcluded, setShowExcluded] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<Prediction>>({});
  
  // Tweet Pipeline state
  const [tweetModalOpen, setTweetModalOpen] = useState(false);
  const [tweetContent, setTweetContent] = useState('');
  const [tweetPrediction, setTweetPrediction] = useState<Prediction | null>(null);
  const [sendingTweet, setSendingTweet] = useState(false);
  
  // Evaluation state
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [evaluationProposals, setEvaluationProposals] = useState<EvaluationProposal[]>([]);
  const [marketContext, setMarketContext] = useState<any>(null);
  const [generatingAITweet, setGeneratingAITweet] = useState<number | null>(null); // Tracks which proposal is generating
  const [auditLoadingId, setAuditLoadingId] = useState<number | null>(null);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [predictionAudit, setPredictionAudit] = useState<PredictionAudit | null>(null);
  const adminToken = localStorage.getItem('adminToken') || '';
  const api = useMemo(() => axios.create({
    baseURL: '/api',
    headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
  }), [adminToken]);

  // Load predictions
  useEffect(() => {
    loadPredictions();
  }, []);

  // Filter predictions
  // Filter and sort predictions - put search matches on top
  useEffect(() => {
    let filtered = predictions;

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.success_status === filterStatus);
    }

    // Asset filter
    if (filterAsset !== 'all') {
      filtered = filtered.filter(p => p.asset_type === filterAsset);
    }

    // Excluded filter
    if (!showExcluded) {
      filtered = filtered.filter(p => !p.exclude_from_chart);
    }

    // Search filter - filter AND sort matches to top
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchScore = (p: Prediction) => {
        let score = 0;
        if (p.id.toString() === search) score += 100; // Exact ID match
        if (p.expert_name?.toLowerCase() === search) score += 50; // Exact name match
        if (p.id.toString().includes(search)) score += 10;
        if (p.expert_name?.toLowerCase().includes(search)) score += 5;
        if (p.quote?.toLowerCase().includes(search)) score += 2;
        if (p.price_prediction?.toLowerCase().includes(search)) score += 1;
        return score;
      };
      
      // Filter to only items with matches
      filtered = filtered.filter(p => matchScore(p) > 0);
      
      // Sort by match score (highest first)
      filtered.sort((a, b) => matchScore(b) - matchScore(a));
    }

    setFilteredPredictions(filtered);
  }, [predictions, searchTerm, filterStatus, filterAsset, showExcluded]);

  // Also filter evaluation proposals when search is active
  const filteredEvaluationProposals = useMemo(() => {
    if (!searchTerm) return evaluationProposals;
    
    const search = searchTerm.toLowerCase();
    return evaluationProposals.filter(p => 
      p.id.toString().includes(search) ||
      p.expert_name?.toLowerCase().includes(search) ||
      p.suggested_status?.toLowerCase().includes(search)
    );
  }, [evaluationProposals, searchTerm]);

  const loadPredictions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/predictions/all');

      if (response.data.success) {
        setPredictions(response.data.predictions);
      }
    } catch (error) {
      console.error('Error loading predictions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prediction: Prediction) => {
    setEditingId(prediction.id);
    setEditData(prediction);
  };

  const handleSave = async (id: number) => {
    try {
      const response = await api.put(`/admin/predictions/${id}`, editData);
      if (response.data.success) {
        await loadPredictions();
        setEditingId(null);
        setEditData({});
      }
    } catch (error) {
      console.error('Error saving prediction:', error);
      alert('Failed to save changes');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleDelete = async (id: number) => {
    console.log('🗑️ Delete clicked for prediction ID:', id);
    if (!window.confirm('Are you sure you want to delete this prediction? This cannot be undone.')) {
      console.log('🗑️ Delete cancelled by user');
      return;
    }

    try {
      console.log('🗑️ Sending delete request to:', `/api/admin/predictions/${id}`);
      const response = await api.delete(`/admin/predictions/${id}`);
      console.log('🗑️ Delete response:', response.data);
      if (response.data.success) {
        alert('Prediction deleted successfully!');
        await loadPredictions();
      } else {
        alert('Delete failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('🗑️ Error deleting prediction:', error);
      alert('Failed to delete prediction: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleExcludeFromChart = async (id: number, currentValue: boolean) => {
    try {
      const response = await api.patch(`/admin/predictions/${id}/exclude`, {
        exclude_from_chart: !currentValue
      });
      if (response.data.success) {
        await loadPredictions();
      }
    } catch (error) {
      console.error('Error toggling exclude status:', error);
      alert('Failed to update');
    }
  };

  const runAudit = async (id: number) => {
    setAuditLoadingId(id);
    try {
      const response = await api.get(`/admin/predictions/${id}/audit`);
      if (response.data.success) {
        setPredictionAudit(response.data.audit);
        setAuditModalOpen(true);
      }
    } catch (error: any) {
      console.error('Error auditing prediction:', error);
      alert('Failed to audit prediction: ' + (error.response?.data?.message || error.message));
    } finally {
      setAuditLoadingId(current => current === id ? null : current);
    }
  };

  const closeAuditModal = () => {
    setAuditModalOpen(false);
    setPredictionAudit(null);
  };

  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return 'N/A';
    }

    return Number(value).toLocaleString();
  };

  // Tweet Pipeline functions
  const generateTweetContent = (prediction: Prediction): string => {
    const name = prediction.expert_name || 'Unknown';
    const price = prediction.price_prediction || prediction.price_min || prediction.price_max || '?';
    const status = prediction.success_status;
    const type = prediction.prediction_type;
    
    if (status === 'success') {
      if (type === 'target') {
        return `✅ PREDICTION CORRECT!\n\n${name} predicted #Bitcoin would hit $${price}.\n\nIt happened! 🎯\n\nTrack all predictions at proofofprice.com\n\n#BTC #Crypto`;
      } else if (type === 'bottom') {
        return `✅ BOTTOM CALL CORRECT!\n\n${name} called the #Bitcoin bottom at $${price}.\n\nPrice never went below! 📈\n\nSee the leaderboard: proofofprice.com\n\n#BTC #Crypto`;
      } else if (type === 'top') {
        return `✅ TOP CALL CORRECT!\n\n${name} said #Bitcoin wouldn't exceed $${price} this cycle.\n\nThey were right! 📉\n\nTrack predictions: proofofprice.com\n\n#BTC #Crypto`;
      }
      return `✅ ${name}'s #Bitcoin prediction was CORRECT!\n\nTarget: $${price}\n\nSee all predictions at proofofprice.com\n\n#BTC #Crypto`;
    } else if (status === 'miss') {
      if (type === 'target') {
        return `❌ PREDICTION MISSED\n\n${name} predicted #Bitcoin would hit $${price}.\n\nTime's up - it didn't happen.\n\nWho will be right? proofofprice.com\n\n#BTC #Crypto`;
      } else if (type === 'bottom') {
        return `❌ BOTTOM CALL MISSED\n\n${name} said #Bitcoin wouldn't go below $${price}.\n\nIt did. 📉\n\nSee who got it right: proofofprice.com\n\n#BTC #Crypto`;
      } else if (type === 'top') {
        return `❌ TOP CALL MISSED\n\n${name} said #Bitcoin wouldn't exceed $${price}.\n\nIt did! 🚀\n\nTrack the leaderboard: proofofprice.com\n\n#BTC #Crypto`;
      }
      return `❌ ${name}'s #Bitcoin prediction missed.\n\nTarget: $${price}\n\nSee who's winning: proofofprice.com\n\n#BTC #Crypto`;
    } else {
      // Pending - announce the prediction
      if (type === 'target') {
        return `🎯 NEW PREDICTION\n\n${name} says #Bitcoin will hit $${price}.\n\nWill they be right? ⏳\n\nTrack it at proofofprice.com\n\n#BTC #Crypto`;
      } else if (type === 'bottom') {
        return `📉 BOTTOM CALL\n\n${name} says #Bitcoin won't go below $${price}.\n\nHODL or fold? ⏳\n\nFollow along: proofofprice.com\n\n#BTC #Crypto`;
      } else if (type === 'top') {
        return `📈 TOP CALL\n\n${name} says #Bitcoin won't exceed $${price} this cycle.\n\nBullish or bearish? ⏳\n\nTrack it: proofofprice.com\n\n#BTC #Crypto`;
      }
      return `🔮 ${name} made a #Bitcoin prediction!\n\nTarget: $${price}\n\nWill they be right? proofofprice.com\n\n#BTC #Crypto`;
    }
  };

  const openTweetModal = (prediction: Prediction) => {
    setTweetPrediction(prediction);
    setTweetContent(generateTweetContent(prediction));
    setTweetModalOpen(true);
  };

  const closeTweetModal = () => {
    setTweetModalOpen(false);
    setTweetPrediction(null);
    setTweetContent('');
  };

  const sendToTweetPipeline = async () => {
    if (!tweetPrediction || !tweetContent.trim()) return;
    
    setSendingTweet(true);
    try {
      const topic = tweetPrediction.success_status === 'pending' 
        ? 'prediction' 
        : 'prediction_resolution';
      
      const response = await api.post('/tweet-pipeline', {
        title: `${tweetPrediction.expert_name} - ${tweetPrediction.prediction_type} prediction`,
        content: tweetContent,
        topic: topic,
        prediction_id: tweetPrediction.id,
        influencer_id: tweetPrediction.influencer_id,
        created_by: 'prediction_manager'
      });
      
      if (response.data.success) {
        alert('✅ Tweet added to pipeline! Go to Tweet Pipeline tab to review and post.');
        closeTweetModal();
      } else {
        alert('Failed to add tweet to pipeline: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error sending to tweet pipeline:', error);
      alert('Failed to send to tweet pipeline: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingTweet(false);
    }
  };

  // Evaluation functions
  const runEvaluation = async () => {
    setEvaluationLoading(true);
    try {
      const response = await api.get('/admin/predictions/evaluate');
      if (response.data.success) {
        setEvaluationProposals(response.data.proposals);
        setMarketContext(response.data.market_context);
        setShowEvaluation(true);
      }
    } catch (error: any) {
      console.error('Error running evaluation:', error);
      alert('Failed to run evaluation: ' + (error.response?.data?.error || error.message));
    } finally {
      setEvaluationLoading(false);
    }
  };

  const applyProposal = async (proposal: EvaluationProposal, alsoCreateTweet: boolean = false) => {
    const confirmMessage = alsoCreateTweet 
      ? `Apply status "${proposal.suggested_status}" to ${proposal.expert_name}'s prediction and create AI-generated tweet?`
      : `Apply status "${proposal.suggested_status}" to ${proposal.expert_name}'s prediction?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      // If creating tweet, show loading state
      if (alsoCreateTweet) {
        setGeneratingAITweet(proposal.id);
      }
      
      const response = await api.put(`/admin/predictions/${proposal.id}`, {
        success_status: proposal.suggested_status
      });
      
      if (response.data.success) {
        // Remove from proposals
        setEvaluationProposals(prev => prev.filter(p => p.id !== proposal.id));
        
        // Refresh predictions to get updated status
        await loadPredictions();
        
        if (alsoCreateTweet) {
          // Generate AI-enhanced tweet content
          try {
            const postType = proposal.suggested_status === 'pending' 
              ? 'new_prediction' 
              : proposal.suggested_status === 'success' 
                ? 'prediction_resolved_correct' 
                : 'prediction_resolved_wrong';
            
            const aiResponse = await api.post('/admin/generate-ai-tweet', {
              prediction_id: proposal.id,
              post_type: postType,
              options: {
                include_person_context: true,
                style: 'engaging'
              }
            });
            
            // Find the updated prediction
            const updatedPred = predictions.find(p => p.id === proposal.id);
            const predWithNewStatus: Prediction = updatedPred 
              ? { ...updatedPred, success_status: proposal.suggested_status }
              : {
                  id: proposal.id,
                  expert_name: proposal.expert_name,
                  prediction_type: proposal.prediction_type,
                  success_status: proposal.suggested_status,
                  price_prediction: proposal.target_price?.toString() || '',
                  price_min: proposal.price_min || undefined,
                  price_max: proposal.price_max || undefined,
                  start_date: null,
                  end_date: proposal.end_date,
                  target_time: null,
                  target_timestamp: null,
                  prediction_made: null,
                  created_at: '',
                  exclude_from_chart: false,
                  quote: proposal.quote
                };
            
            setTweetPrediction(predWithNewStatus);
            
            // Use AI-generated content if successful, fallback to template
            if (aiResponse.data.success && aiResponse.data.tweet) {
              console.log('🤖 Using AI-generated tweet:', aiResponse.data.method);
              setTweetContent(aiResponse.data.tweet);
            } else {
              console.log('📝 Using template-based tweet (AI fallback)');
              setTweetContent(generateTweetContent(predWithNewStatus));
            }
            
            setTweetModalOpen(true);
          } catch (aiError: any) {
            console.error('AI tweet generation failed:', aiError);
            // Fallback to template-based generation
            const updatedPred = predictions.find(p => p.id === proposal.id);
            const predWithNewStatus = updatedPred 
              ? { ...updatedPred, success_status: proposal.suggested_status }
              : {
                  id: proposal.id,
                  expert_name: proposal.expert_name,
                  prediction_type: proposal.prediction_type,
                  success_status: proposal.suggested_status,
                  price_prediction: proposal.target_price?.toString() || '',
                  price_min: proposal.price_min || undefined,
                  price_max: proposal.price_max || undefined,
                  start_date: null,
                  end_date: proposal.end_date,
                  target_time: null,
                  target_timestamp: null,
                  prediction_made: null,
                  created_at: '',
                  exclude_from_chart: false,
                  quote: proposal.quote
                } as Prediction;
            
            setTweetPrediction(predWithNewStatus);
            setTweetContent(generateTweetContent(predWithNewStatus));
            setTweetModalOpen(true);
          } finally {
            setGeneratingAITweet(null);
          }
        } else {
          alert(`✅ Updated ${proposal.expert_name}'s prediction to "${proposal.suggested_status}"`);
        }
      }
    } catch (error: any) {
      setGeneratingAITweet(null);
      console.error('Error applying proposal:', error);
      alert('Failed to apply proposal: ' + (error.response?.data?.error || error.message));
    }
  };

  const dismissProposal = (proposalId: number) => {
    setEvaluationProposals(prev => prev.filter(p => p.id !== proposalId));
  };

  // Calculate stats
  const stats = {
    total: predictions.length,
    pending: predictions.filter(p => p.success_status === 'pending').length,
    success: predictions.filter(p => p.success_status === 'success').length,
    miss: predictions.filter(p => p.success_status === 'miss').length,
    excluded: predictions.filter(p => p.exclude_from_chart).length
  };

  if (loading) {
    return <Container><Title>Loading predictions...</Title></Container>;
  }

  return (
    <Container>
      <Header>
        <Title>🎯 Prediction Manager</Title>
        <SearchBar
          type="text"
          placeholder="Search by ID, expert name, quote, price..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Header>

      <StatsRow>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#eab308' }}>{stats.pending}</StatNumber>
          <StatLabel>Pending</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#16a34a' }}>{stats.success}</StatNumber>
          <StatLabel>Success</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#dc2626' }}>{stats.miss}</StatNumber>
          <StatLabel>Miss</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#6b7280' }}>{stats.excluded}</StatNumber>
          <StatLabel>Excluded</StatLabel>
        </StatCard>
        <StatCard 
          style={{ cursor: 'pointer', background: showEvaluation ? '#0f3460' : '#0f0f0f' }}
          onClick={runEvaluation}
        >
          <StatNumber style={{ color: '#e94560' }}>
            {evaluationLoading ? '⏳' : '🔍'}
          </StatNumber>
          <StatLabel>{evaluationLoading ? 'Running...' : 'Run Evaluation'}</StatLabel>
        </StatCard>
      </StatsRow>

      {/* Evaluation Section */}
      {showEvaluation && filteredEvaluationProposals.length > 0 && (
        <EvaluationSection>
          <EvaluationHeader>
            <EvaluationTitle>
              🔍 Evaluation Proposals ({filteredEvaluationProposals.filter(p => p.action_required).length} action required)
              {searchTerm && ` - filtered by "${searchTerm}"`}
            </EvaluationTitle>
            <ActionButton onClick={() => setShowEvaluation(false)}>
              ✕ Close
            </ActionButton>
          </EvaluationHeader>
          
          {marketContext && (
            <MarketContext>
              <MarketStat>
                <div className="value">${marketContext.current_price?.toLocaleString()}</div>
                <div className="label">Current BTC</div>
              </MarketStat>
              <MarketStat>
                <div className="value">${marketContext.yearly_high?.toLocaleString()}</div>
                <div className="label">2025 High</div>
              </MarketStat>
              <MarketStat>
                <div className="value">${marketContext.yearly_low?.toLocaleString()}</div>
                <div className="label">2025 Low</div>
              </MarketStat>
            </MarketContext>
          )}
          
          {filteredEvaluationProposals.map(proposal => (
            <ProposalCard 
              key={proposal.id} 
              actionRequired={proposal.action_required}
              suggestionType={proposal.suggested_status}
            >
              <ProposalHeader>
                <ProposalInfo>
                  <ProposalName>
                    #{proposal.id} {proposal.expert_name}
                    {proposal.action_required && <span style={{ color: '#e94560', marginLeft: '8px' }}>⚠️ ACTION REQUIRED</span>}
                  </ProposalName>
                  <ProposalMeta>
                    <span>{proposal.prediction_type.toUpperCase()}</span>
                    <span>Target: ${proposal.target_price?.toLocaleString() || 'N/A'}</span>
                    <span>{proposal.is_expired ? '⏰ EXPIRED' : `${proposal.days_left} days left`}</span>
                    <span>Confidence: {proposal.confidence}</span>
                  </ProposalMeta>
                </ProposalInfo>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <SuggestionBadge type={proposal.suggested_status}>
                    → {proposal.suggested_status}
                  </SuggestionBadge>
                </div>
              </ProposalHeader>
              
              <ProposalReason color={proposal.suggested_status === 'success' ? '#16a34a' : proposal.suggested_status === 'miss' ? '#dc2626' : '#eab308'}>
                {proposal.reason || 'No specific reason provided'}
              </ProposalReason>
              
              {proposal.context && (
                <ProposalContext>
                  <span>📊 Creation: ${proposal.context.creation_price?.toLocaleString() || 'N/A'}</span>
                  <span>📈 Period High: ${proposal.context.period_high?.toLocaleString()}</span>
                  <span>📉 Period Low: ${proposal.context.period_low?.toLocaleString()}</span>
                </ProposalContext>
              )}
              
              {proposal.quote && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                  "{proposal.quote}..."
                </div>
              )}
              
              {proposal.source_url && (
                <a 
                  href={proposal.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    marginTop: '8px', 
                    color: '#1DA1F2', 
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  🔗 View Original Tweet {proposal.x_handle && `(@${proposal.x_handle})`}
                </a>
              )}
              
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                {proposal.suggested_status !== 'pending' && (
                  <>
                    <ApplyButton 
                      variant={proposal.suggested_status as 'success' | 'miss'}
                      onClick={() => applyProposal(proposal, true)}
                      style={{ fontWeight: 'bold' }}
                      disabled={generatingAITweet !== null}
                    >
                      {generatingAITweet === proposal.id ? '🤖 Generating AI Tweet...' : '✓ Apply & Tweet 📢'}
                    </ApplyButton>
                    <ActionButton 
                      onClick={() => applyProposal(proposal, false)}
                      style={{ opacity: 0.8 }}
                      disabled={generatingAITweet !== null}
                    >
                      Apply Only
                    </ActionButton>
                  </>
                )}
                <ActionButton 
                  onClick={() => {
                    const pred = predictions.find(p => p.id === proposal.id);
                    if (pred) openTweetModal(pred);
                  }}
                  disabled={generatingAITweet !== null}
                >
                  📢 Tweet (No Apply)
                </ActionButton>
                <ActionButton 
                  onClick={() => dismissProposal(proposal.id)}
                  disabled={generatingAITweet !== null}
                >
                  Dismiss
                </ActionButton>
              </div>
            </ProposalCard>
          ))}
        </EvaluationSection>
      )}

      {showEvaluation && evaluationProposals.length === 0 && !evaluationLoading && (
        <EvaluationSection>
          <p style={{ color: '#888', textAlign: 'center', margin: '20px 0' }}>
            ✅ No pending predictions require immediate action. All looking good!
          </p>
          <div style={{ textAlign: 'center' }}>
            <ActionButton onClick={() => setShowEvaluation(false)}>Close</ActionButton>
          </div>
        </EvaluationSection>
      )}

      <FilterSection>
        <FilterButton active={filterStatus === 'all'} onClick={() => setFilterStatus('all')}>
          All Status
        </FilterButton>
        <FilterButton active={filterStatus === 'pending'} onClick={() => setFilterStatus('pending')}>
          Pending
        </FilterButton>
        <FilterButton active={filterStatus === 'success'} onClick={() => setFilterStatus('success')}>
          Success
        </FilterButton>
        <FilterButton active={filterStatus === 'miss'} onClick={() => setFilterStatus('miss')}>
          Miss
        </FilterButton>
        
        <div style={{ width: '20px' }} />
        
        <FilterButton active={filterAsset === 'all'} onClick={() => setFilterAsset('all')}>
          All Assets
        </FilterButton>
        <FilterButton active={filterAsset === 'btc'} onClick={() => setFilterAsset('btc')}>
          BTC
        </FilterButton>
        <FilterButton active={filterAsset === 'mstr'} onClick={() => setFilterAsset('mstr')}>
          MSTR
        </FilterButton>
        
        <div style={{ width: '20px' }} />
        
        <ToggleSwitch>
          <input
            type="checkbox"
            checked={showExcluded}
            onChange={(e) => setShowExcluded(e.target.checked)}
          />
          <span>Show Excluded</span>
        </ToggleSwitch>
      </FilterSection>

      <div>
        <p style={{ color: '#888', marginBottom: '15px' }}>
          Showing {filteredPredictions.length} of {predictions.length} predictions
        </p>

        {filteredPredictions.map(prediction => (
          <PredictionCard key={prediction.id} excluded={prediction.exclude_from_chart}>
            <CardHeader>
              <ExpertInfo>
                <ExpertName>
                  {prediction.expert_name || 'Unknown Expert'}
                  {prediction.exclude_from_chart && <span style={{ color: '#dc2626', marginLeft: '10px' }}>🚫 EXCLUDED</span>}
                </ExpertName>
                <PredictionDetails>
                  ID: #{prediction.id} | Type: {prediction.prediction_type} | 
                  <Badge status={prediction.success_status} style={{ marginLeft: '8px' }}>
                    {prediction.success_status}
                  </Badge>
                </PredictionDetails>
                <PredictionDetails>
                  Price: ${prediction.price_prediction} | Target: {prediction.target_time || prediction.end_date || 'N/A'}
                </PredictionDetails>
                {prediction.price_min && prediction.price_max && (
                  <PredictionDetails>
                    Range: ${prediction.price_min?.toLocaleString()} - ${prediction.price_max?.toLocaleString()}
                  </PredictionDetails>
                )}
              </ExpertInfo>
              
              <ActionButtons>
                {editingId !== prediction.id && (
                  <>
                    <ActionButton onClick={() => handleEdit(prediction)}>
                      ✏️ Edit
                    </ActionButton>
                    <ActionButton onClick={() => openTweetModal(prediction)}>
                      📢 Tweet
                    </ActionButton>
                    <ActionButton onClick={() => runAudit(prediction.id)}>
                      {auditLoadingId === prediction.id ? '⏳ Auditing...' : '🧪 Audit'}
                    </ActionButton>
                    <ActionButton 
                      variant={prediction.exclude_from_chart ? 'success' : 'danger'}
                      onClick={() => toggleExcludeFromChart(prediction.id, prediction.exclude_from_chart)}
                    >
                      {prediction.exclude_from_chart ? '✅ Include' : '🚫 Exclude'}
                    </ActionButton>
                    <ActionButton variant="danger" onClick={() => handleDelete(prediction.id)}>
                      🗑️ Delete
                    </ActionButton>
                  </>
                )}
              </ActionButtons>
            </CardHeader>

            {prediction.quote && !editingId && (
              <QuoteBox>{prediction.quote}</QuoteBox>
            )}

            {prediction.source_url && !editingId && (
              <PredictionDetails>
                <a href={prediction.source_url} target="_blank" rel="noopener noreferrer" style={{ color: '#4299e1' }}>
                  🔗 Source
                </a>
              </PredictionDetails>
            )}

            {editingId === prediction.id && (
              <EditForm>
                <FormRow>
                  <FormGroup>
                    <Label>Price Prediction</Label>
                    <Input
                      value={editData.price_prediction || ''}
                      onChange={(e) => setEditData({ ...editData, price_prediction: e.target.value })}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Prediction Type</Label>
                    <Select
                      value={editData.prediction_type || ''}
                      onChange={(e) => setEditData({ ...editData, prediction_type: e.target.value })}
                    >
                      <option value="target">Target - Price will reach X</option>
                      <option value="bottom">Bottom - Price won't go below X</option>
                      <option value="top">Top - Price won't go above X</option>
                      <option value="range">Range - Price between X and Y</option>
                      <option value="pricetimerange">Price Time Range - Above X by time Y</option>
                      <option value="bet">Bet - Wager-based prediction</option>
                      <option value="sentiment">Sentiment - Market direction/emotion</option>
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Success Status</Label>
                    <Select
                      value={editData.success_status || ''}
                      onChange={(e) => setEditData({ ...editData, success_status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="success">Success</option>
                      <option value="miss">Miss</option>
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Price Min</Label>
                    <Input
                      type="number"
                      value={editData.price_min || ''}
                      onChange={(e) => setEditData({ ...editData, price_min: parseFloat(e.target.value) || undefined })}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Price Max</Label>
                    <Input
                      type="number"
                      value={editData.price_max || ''}
                      onChange={(e) => setEditData({ ...editData, price_max: parseFloat(e.target.value) || undefined })}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={editData.start_date?.split('T')[0] || ''}
                      onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>End Date / Target Time</Label>
                    <Input
                      type="date"
                      value={editData.end_date?.split('T')[0] || editData.target_time?.split('T')[0] || ''}
                      onChange={(e) => setEditData({ ...editData, end_date: e.target.value, target_time: e.target.value })}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup style={{ marginBottom: '15px' }}>
                  <Label>Quote</Label>
                  <TextArea
                    value={editData.quote || ''}
                    onChange={(e) => setEditData({ ...editData, quote: e.target.value })}
                    placeholder="The exact prediction quote..."
                  />
                </FormGroup>

                <FormGroup style={{ marginBottom: '15px' }}>
                  <Label>Source URL</Label>
                  <Input
                    value={editData.source_url || ''}
                    onChange={(e) => setEditData({ ...editData, source_url: e.target.value })}
                    placeholder="https://..."
                  />
                </FormGroup>

                <ActionButtons>
                  <ActionButton variant="success" onClick={() => handleSave(prediction.id)}>
                    💾 Save Changes
                  </ActionButton>
                  <ActionButton onClick={handleCancel}>
                    ❌ Cancel
                  </ActionButton>
                </ActionButtons>
              </EditForm>
            )}
          </PredictionCard>
        ))}
      </div>

      {/* Tweet Modal */}
      {tweetModalOpen && tweetPrediction && (
        <TweetModal onClick={(e) => e.target === e.currentTarget && closeTweetModal()}>
          <TweetModalContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>
                📢 Create Tweet for #{tweetPrediction.id}
              </h3>
              <button 
                onClick={closeTweetModal}
                style={{ background: 'none', border: 'none', color: '#888', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#888', fontSize: '14px', margin: '0 0 8px 0' }}>
                {tweetPrediction.expert_name} | {tweetPrediction.prediction_type.toUpperCase()} | 
                <Badge status={tweetPrediction.success_status} style={{ marginLeft: '8px' }}>
                  {tweetPrediction.success_status}
                </Badge>
              </p>
            </div>

            <Label style={{ color: '#888', marginBottom: '8px', display: 'block' }}>Tweet Content</Label>
            <TweetTextArea
              value={tweetContent}
              onChange={(e) => setTweetContent(e.target.value)}
              placeholder="Compose your tweet..."
            />
            <CharCount over={false}>
              {tweetContent.length} characters
            </CharCount>

            <TweetPreview>
              <h4>Preview</h4>
              <p>{tweetContent}</p>
            </TweetPreview>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <ActionButton onClick={closeTweetModal}>
                Cancel
              </ActionButton>
              <ActionButton 
                variant="success" 
                onClick={sendToTweetPipeline}
                disabled={sendingTweet || !tweetContent.trim()}
              >
                {sendingTweet ? '⏳ Sending...' : '🚀 Add to Tweet Pipeline'}
              </ActionButton>
            </div>
          </TweetModalContent>
        </TweetModal>
      )}

      {auditModalOpen && predictionAudit && (
        <TweetModal onClick={(e) => e.target === e.currentTarget && closeAuditModal()}>
          <TweetModalContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>
                🧪 Evaluation Audit for #{predictionAudit.id}
              </h3>
              <button
                onClick={closeAuditModal}
                style={{ background: 'none', border: 'none', color: '#888', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#aaa', marginTop: 0 }}>
              {predictionAudit.expert_name || 'Unknown expert'}{predictionAudit.x_handle ? ` (@${predictionAudit.x_handle})` : ''} | {predictionAudit.prediction_type.toUpperCase()}
            </p>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <Badge status={predictionAudit.current_status || undefined}>Current: {predictionAudit.current_status || 'null'}</Badge>
              <Badge status={predictionAudit.suggested_status}>Suggested: {predictionAudit.suggested_status}</Badge>
              <Badge>Confidence: {predictionAudit.confidence}</Badge>
              {predictionAudit.requires_manual_check && <Badge>Manual Review</Badge>}
            </div>

            <AuditReason>{predictionAudit.reason}</AuditReason>

            <AuditMetaGrid>
              <AuditStat>
                <div className="label">Window</div>
                <div className="value">{predictionAudit.start_date || 'N/A'} {'->'} {predictionAudit.end_date || 'N/A'}</div>
              </AuditStat>
              <AuditStat>
                <div className="label">Period Status</div>
                <div className="value">{predictionAudit.period_status || 'N/A'}</div>
              </AuditStat>
              <AuditStat>
                <div className="label">Target</div>
                <div className="value">${formatNumber(predictionAudit.target_price)}</div>
              </AuditStat>
              {(predictionAudit.range_min !== null || predictionAudit.range_max !== null) && (
                <AuditStat>
                  <div className="label">Range</div>
                  <div className="value">${formatNumber(predictionAudit.range_min)} {'->'} ${formatNumber(predictionAudit.range_max)}</div>
                </AuditStat>
              )}
              <AuditStat>
                <div className="label">Period High</div>
                <div className="value">${formatNumber(predictionAudit.period_high)}</div>
              </AuditStat>
              <AuditStat>
                <div className="label">Period Low</div>
                <div className="value">${formatNumber(predictionAudit.period_low)}</div>
              </AuditStat>
              <AuditStat>
                <div className="label">Price Data Points</div>
                <div className="value">{predictionAudit.price_data_points ?? 'N/A'}</div>
              </AuditStat>
              <AuditStat>
                <div className="label">Start Price Ref</div>
                <div className="value">${formatNumber(predictionAudit.start_price)}{predictionAudit.start_price_date ? ` on ${predictionAudit.start_price_date}` : ''}</div>
              </AuditStat>
              {predictionAudit.prediction_direction && (
                <AuditStat>
                  <div className="label">Direction</div>
                  <div className="value">{predictionAudit.prediction_direction}</div>
                </AuditStat>
              )}
              {predictionAudit.break_date && (
                <AuditStat>
                  <div className="label">Break Event</div>
                  <div className="value">{predictionAudit.break_date} at ${formatNumber(predictionAudit.break_price)}</div>
                </AuditStat>
              )}
            </AuditMetaGrid>

            {predictionAudit.issues.length > 0 && (
              <AuditIssueList>
                {predictionAudit.issues.map((issue, index) => (
                  <div key={`${predictionAudit.id}-issue-${index}`}>⚠️ {issue}</div>
                ))}
              </AuditIssueList>
            )}

            {predictionAudit.quote && (
              <QuoteBox>{predictionAudit.quote}</QuoteBox>
            )}

            {predictionAudit.source_url && (
              <div style={{ marginTop: '12px' }}>
                <a href={predictionAudit.source_url} target="_blank" rel="noopener noreferrer" style={{ color: '#4299e1' }}>
                  🔗 View Original Source
                </a>
              </div>
            )}
          </TweetModalContent>
        </TweetModal>
      )}
    </Container>
  );
};

export default PredictionManager;
